import React from 'react';

const sections = [
  {
    title: '1. What BillJaldi Is',
    body: [
      'BillJaldi is an invoice creation, storage, sharing, and business workflow tool for Indian businesses. The service helps users create GST-style invoices, store account history, manage customers and items, share invoices, and access paid plan features.',
      'BillJaldi is not a chartered accountant, tax advisor, legal advisor, payment institution, or government filing system. Users are responsible for verifying the accuracy, legality, tax treatment, GSTINs, HSN/SAC codes, invoice values, and all information entered into the service.',
    ],
  },
  {
    title: '2. Information We Collect',
    body: [
      'We may collect account information such as name, email address, login provider, phone number, business name, business address, GSTIN, state, logo, seal/signature image, plan, subscription status, and payment metadata.',
      'We may collect invoice and business workflow information entered by users, including customer/client names, phone numbers, email addresses, GSTINs, addresses, invoice line items, invoice amounts, due dates, payment status, notes, saved products/items, and invoice documents or previews.',
      'We may collect technical information such as browser, device, IP-derived region, pages visited, timestamps, errors, security events, and usage actions needed to operate, debug, secure, and improve BillJaldi.',
    ],
  },
  {
    title: '3. Why We Use This Information',
    body: [
      'We use account, business, customer, invoice, and technical information to provide the BillJaldi service, authenticate users, save invoice history, generate PDFs, enforce plan limits, process subscriptions, support customer workflows, prevent abuse, debug issues, improve reliability, and comply with applicable law.',
      'Customer/client details entered by users may be used to support invoice sharing, reminders, due-date workflows, payment follow-ups, customer outstanding tracking, business reporting, and other product features requested or enabled by the user.',
      'We may use operational and aggregated information for product improvement, analytics, fraud prevention, security, pricing, support, compliance, and legally permitted business purposes. We do not sell customer/client contact lists.',
    ],
  },
  {
    title: '4. WhatsApp, Email, Reminders, And Integrations',
    body: [
      'If a user chooses to share invoices, send reminders, connect integrations, or use future workflow features, BillJaldi may process the relevant business, invoice, customer/client, due-date, and payment-status information necessary to perform that action.',
      'Users must ensure they have the right to contact their customers or clients and must comply with applicable consent, communication, platform, telecom, privacy, and anti-spam requirements.',
    ],
  },
  {
    title: '5. Payments And Subscriptions',
    body: [
      'Paid plans may be processed through Razorpay or another payment provider. Payment providers may process payment method details according to their own terms and privacy policies. BillJaldi stores payment metadata such as plan, amount, subscription ID, payment ID, status, and billing period so that plan access can be managed.',
      'If a subscription payment is not received for a renewal period, access may automatically fall back to the free plan after the paid period ends, unless a valid manual trial or admin override is active.',
    ],
  },
  {
    title: '6. Data Storage, Retention, And Deletion',
    body: [
      'Logged-in account data is stored on BillJaldi systems. Logged-out invoice creation may be temporary and may not be recoverable. When a user logs out, BillJaldi may clear local browser drafts, local history, locally saved customers, and locally saved items from that device.',
      'We may retain account, billing, invoice, technical, and security records as needed to provide the service, comply with legal obligations, resolve disputes, prevent fraud, maintain backups, and enforce agreements.',
    ],
  },
  {
    title: '7. User Responsibilities',
    body: [
      'Users are responsible for the information they enter, the invoices they issue, the recipients they contact, and the business, tax, accounting, and legal consequences of using generated invoices.',
      'Users must not use BillJaldi for fraud, impersonation, unlawful billing, spam, harassment, tax evasion, misleading invoices, or any activity that violates applicable law or third-party rights.',
    ],
  },
  {
    title: '8. Security',
    body: [
      'We use reasonable technical and organizational measures to protect data, including HTTPS for production API traffic and access controls for account-specific data. No system is perfectly secure, and users should protect their Google account, devices, browsers, and access credentials.',
    ],
  },
  {
    title: '9. Limitation Of Liability',
    body: [
      'BillJaldi is provided on an as-is and as-available basis. To the maximum extent permitted by law, BillJaldi is not liable for incorrect invoice data entered by users, tax or GST compliance errors, business losses, missed payments, customer disputes, platform outages, third-party service failures, or indirect, incidental, special, consequential, or punitive damages.',
    ],
  },
  {
    title: '10. Changes And Contact',
    body: [
      'We may update these terms and policies as the product evolves. Continued use of BillJaldi after changes means the updated terms apply.',
      'For account, privacy, billing, or legal queries, contact BillJaldi support through the product or the official contact channel published on the website.',
    ],
  },
];

export default function Legal() {
  return (
    <div className="max-w-[980px] mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="legal-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.18em] font-black text-orange-600 mb-2">Legal</div>
        <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tight text-slate-900">Terms and data use</h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-500">
          This page explains how BillJaldi operates, what information may be processed, and what users are responsible for when creating and sharing invoices.
        </p>
        <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-600">Bill Jaldi is operated by Carpentry Works.</p>
        <p className="mt-2 text-xs font-semibold text-slate-400">Last updated: June 2026</p>
      </div>

      <div className="space-y-4">
        {sections.map((section) => (
          <section key={section.title} className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
            <h2 className="font-heading text-xl font-black text-slate-900">{section.title}</h2>
            <div className="mt-3 space-y-3">
              {section.body.map((paragraph) => (
                <p key={paragraph} className="text-sm leading-7 text-slate-600">{paragraph}</p>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
